import React, { useState, useRef, useEffect, useMemo } from 'react'
import {
  useProductState,
  useProductFunctions,
  useScrollTo,
  getElementOffset,
  useSizesReducer,
  OptionsType,
  Portal,
  useConfigState,
  useLocalLabels,
  canUseDOM,
  useGlobalLabels,
  usePrice,
  MoneyDTO,
  useLabelsQuery,
} from '@sentecacommerce-theme/lib'
import styled, { css } from 'styled-components'
import dynamic from 'next/dynamic'
import { useInView } from 'react-intersection-observer'
import {
  DeliveryText,
  IconPdf,
  Breadcrumbs,
  Collapse,
  IconArrowDown,
  WishlistButton,
} from '../../../Common'

import {
  ProductContent,
  ProductInfo,
  AddToBasket,
  ProductImage,
  MobileStickyAddToBasket,
  Notice,
  AvailableOptionsSelect,
} from '..'
import { ProductPageLabels } from '../../productPage.props'

import { ProductTab } from '../../productPage.props'
import { VariantSelector } from '../VariantsSelector'
import { injectScript } from './utils/injectScript'
import { mapLabels } from '../../../utils/LabelUtils'

const RelatedCarousels = dynamic<{
  isCategoryCarouselActive: boolean
  isProductsCarouselActive: boolean
}>(() => import('../RelatedCarousels').then((mod) => mod.RelatedCarousels))

type ProductComponentProps = {
  tabs: ProductTab[]
  isCategoryCarouselActive: boolean
  isProductsCarouselActive: boolean
}

export const ProductComponent = ({
  tabs: globalTabs,
  isCategoryCarouselActive,
  isProductsCarouselActive,
}: ProductComponentProps) => {
  const globalLabels = useGlobalLabels()
  const { state, clearSize, setSize } = useSizesReducer()
  const config = useConfigState()
  const sizesContainerRef = useRef<HTMLDivElement | null>(null)
  const labels = useLocalLabels<ProductPageLabels>()
  const [addToBasketRef, isAddToBasketInView] = useInView({
    threshold: 0.2,
  })
  const [fakeElementRef, isFooterInView] = useInView({
    threshold: 0.8,
  })

  const { metaData, attributeRules, selectedVariant } = useProductState()
  const { scrollTo } = useScrollTo(0, 2)

  const { getCustomFieldValue } = useProductFunctions()

  const hideRegularPrice = getCustomFieldValue?.(
    config?.customFieldKeys?.hideRegularPrice
  )

  const priceProps = selectedVariant?.offer?.originalPrice
  const discountedPriceProps = selectedVariant?.offer?.discountedPrice

  const getPrice = () => {
    if (!hideRegularPrice) return priceProps

    if (discountedPriceProps?.centAmount) return discountedPriceProps

    return priceProps
  }

  const { price, discountPercentage, discountDifference } = usePrice({
    price: getPrice(),
    discountedPrice: !hideRegularPrice ? discountedPriceProps : undefined,
  })

  const [isSizeError, setIsSizeError] = useState(false)

  const isInAction = getCustomFieldValue(config.customFieldKeys.isAction)

  const discountIcon = getCustomFieldValue(config.customFieldKeys.discountIcon)

  const [imageIndex, setImageIndex] = useState<number | undefined>(undefined)

  const [expand, setExpand] = useState(false)
  const [showOptionError, setShowOptionError] = useState(false)

  const optionsArray = selectedVariant?.options as OptionsType[]
  const hasOptions = Boolean(optionsArray?.length > 0)

  const queryLabelsKeys = selectedVariant?.labels as string[]
  const { data: queryLabels } = useLabelsQuery({ labels: queryLabelsKeys })

  const mappedLabels = useMemo(
    () =>
      mapLabels({ labels: queryLabels?.results || [], isInAction: isInAction }),
    [queryLabels?.results]
  )

  const tabs = metaData.description
    ? [
        {
          name: labels?.description,
          content: metaData.description,
        },
        ...globalTabs,
        ...(metaData.content || []),
      ]
    : [...globalTabs, ...(metaData.content || [])]

  const isWithFixedDelivery = getCustomFieldValue(
    config.customFieldKeys.isWithFixedDelivery
  )

  const allColors = attributeRules?.[config.attributeKeys.color]?.values

  const instructions = metaData?.assets?.instructions

  const selectedOptions = selectedVariant?.selectedOptions ?? []
  const forceToSetOption = hasOptions && !Boolean(selectedOptions?.length > 0)

  const hasSizeAttribute = Boolean(
    selectedVariant?.getAttribute(config.attributeKeys.size)
  )
  const isSizeSelected = hasSizeAttribute && state?.isSizeSelected

  const showErrorOnAddClick =
    (!state?.isSizeSelected && Boolean(allColors)) ||
    forceToSetOption ||
    (hasSizeAttribute && !isSizeSelected)

  const onAddClick = (shouldScrollTo = false) => {
    if (forceToSetOption) setShowOptionError(true)

    if (shouldScrollTo)
      scrollTo(getElementOffset(sizesContainerRef.current).top + 400)

    setTimeout(
      () => {
        setIsSizeError(true)
        if (!sizesContainerRef.current) return
      },
      shouldScrollTo ? 500 : 0
    )
  }

  const isAventBrand = Boolean(metaData?.brand?.name === 'Philips Avent')
  const hasContent =
    isAventBrand &&
    Boolean(
      canUseDOM() &&
        document.getElementById('flix-minisite')?.children?.item(0)?.tagName ===
          'A'
    )

  useEffect(() => {
    if (!forceToSetOption) {
      setShowOptionError(false)
    }
  }, [forceToSetOption, isAventBrand, selectedVariant])

  useEffect(() => {
    if (isAventBrand) {
      injectScript(selectedVariant)
    }
  }, [isAventBrand, selectedVariant])

  const isRelatedCarouselsActive =
    (isCategoryCarouselActive && metaData?.category?._id) ||
    (isProductsCarouselActive && metaData?.brand?._id)

  const discountValue = discountDifference?.value

  const isOnlyInHippoland = getCustomFieldValue?.(
    config?.customFieldKeys?.isOnlyInHippoland
  )

  const isNew = getCustomFieldValue?.(config?.customFieldKeys?.isNew)

  const isOnlineOnly = getCustomFieldValue?.(
    config?.customFieldKeys?.isOnlineOnly
  )

  const isBestSelling = false

  return (
    <>
      <Breadcrumbs
        data={[
          ...(metaData?.categories || []),

          {
            name: metaData.name || '',
            id: metaData?.category?._id || '',
            link: `/${metaData.slug}/p`,
          },
        ]}
      />

      <div className="pt-lg-5 mt-lg-3">
        <Container>
          <Flex>
            <ImageListContainer>
              <ProductImage
                labels={mappedLabels}
                imageIndex={imageIndex || 0}
              />
              {discountIcon && (
                <DiscountIcon
                  alt="discount"
                  src={((discountIcon as unknown) as string) || 'Hippoland'}
                  isInAction={isInAction}
                />
              )}

              <FavIconWrapper>
                <WishlistButton
                  productId={metaData._id as string}
                  variantId={selectedVariant?._id as string}
                />
              </FavIconWrapper>

              <BadgeListContainer>
                {isInAction && (
                  <ActionImageContainer>
                    <img
                      style={{ width: '5rem' }}
                      alt="Sale"
                      src="https://www.hippoland.net/media/catalog/category/label-action-icon.svg"
                    />
                  </ActionImageContainer>
                )}

                {isOnlyInHippoland && (
                  <YellowBadge>{globalLabels?.onlyInHippoland}</YellowBadge>
                )}

                {isNew && <NewBadge>{globalLabels?.new}</NewBadge>}
                {isOnlineOnly && (
                  <BlueBadge>{globalLabels.onlyOnline}</BlueBadge>
                )}

                {isBestSelling && (
                  <MostSaled>{globalLabels?.bestSeller}</MostSaled>
                )}

                {!isInAction && Boolean(discountPercentage) && (
                  <DiscountBadge>-{discountPercentage}%</DiscountBadge>
                )}

                {!isInAction && !Boolean(discountPercentage) && discountValue && (
                  <DiscountBadge>
                    -{Math.floor(discountValue)}
                    <small>{price.currency}</small>
                  </DiscountBadge>
                )}
                {discountIcon && (
                  <DiscountIcon
                    src={((discountIcon as unknown) as string) || 'Hippoland'}
                  />
                )}
              </BadgeListContainer>
            </ImageListContainer>
            <InfoContainer>
              <Padding className="pb-0">
                <ProductInfo />
                <VariantSelector
                  resetSize={clearSize}
                  selectedSizeId={state.sizeId}
                  setSize={setSize}
                  sizesContainerRef={sizesContainerRef}
                  isSizeError={!state.isSizeSelected && isSizeError}
                />
              </Padding>
              <AddToBasketAndOptionsContainer>
                {hasOptions && (
                  <AvailableOptionsSelect
                    setImageIndex={setImageIndex}
                    selectOptionDefaultLabel={labels?.selectOptionDefaultLabel}
                    showOptionError={showOptionError}
                  />
                )}
                <AddToBasket
                  onClick={
                    showErrorOnAddClick ? () => onAddClick(false) : undefined
                  }
                  containerRef={addToBasketRef}
                />
              </AddToBasketAndOptionsContainer>

              {!isWithFixedDelivery && labels?.freeDeliveryLabel && (
                <Padding className="py-0">
                  <DeliveryText isCentered>
                    {labels?.freeDeliveryLabel}
                  </DeliveryText>
                </Padding>
              )}

              {isWithFixedDelivery && (
                <Padding className="py-0">
                  <DeliveryText>{labels?.fixedPriceLabel}</DeliveryText>
                </Padding>
              )}

              {labels?.availabilityNotice && (
                <div className="px-3 pt-3">
                  <Notice notice={labels?.availabilityNotice} />
                </div>
              )}

              <MobileStickyAddToBasket
                onAddToBasketClick={
                  showErrorOnAddClick ? () => onAddClick(true) : undefined
                }
                show={!isAddToBasketInView && !isFooterInView}
              />

              <ContentContainer>
                {Boolean(instructions) && (
                  <Instructions
                    target="_blank"
                    rel="noreferrer"
                    aria-label={instructions?.name}
                    title={instructions?.name}
                    href={instructions?.sources[0].url}
                  >
                    <IconPdf />
                    {instructions?.name}
                  </Instructions>
                )}
                <ProductContent tabs={tabs} description={labels.description} />
              </ContentContainer>
            </InfoContainer>
          </Flex>
        </Container>
        {isAventBrand && (
          <AdditionalDescriptionAventContainer
            expand={expand}
            hasContent={hasContent}
          >
            <Collapse expanded={expand} collapseHeight="12.5rem">
              <AdditionalDescriptionAvent id="flix-inpage"></AdditionalDescriptionAvent>
            </Collapse>
            <IconArrowDown onClick={() => setExpand(!expand)} />
          </AdditionalDescriptionAventContainer>
        )}
      </div>
      {isRelatedCarouselsActive && (
        <RelatedCarousels
          isCategoryCarouselActive={isCategoryCarouselActive}
          isProductsCarouselActive={isProductsCarouselActive}
        />
      )}
      <Portal>
        <div
          ref={fakeElementRef}
          style={{
            position: 'absolute',
            height: 1000,
            top: '100%',
            transform: 'translateY(-100%)',
            left: 0,
            width: '100%',
            pointerEvents: 'none',
          }}
        />
      </Portal>
    </>
  )
}

const Padding = styled.div.attrs<{ className?: string }>({
  className: 'p-3',
})``

const Flex = styled.div.attrs({
  className: 'row g-4 justify-content-center',
})``

const AddToBasketAndOptionsContainer = styled.div`
  margin: 1.8rem 0;
`

const Instructions = styled(Padding).attrs({
  as: 'a',
  className: 'py-4',
})<any>`
  display: flex;
  align-items: center;
  color: ${({ theme }) => theme.palette.greyTitle};

  & > svg {
    width: 2.8rem;
    margin-right: 1.3rem;
  }

  &:hover {
    text-decoration: underline;
  }
`

const Container = styled.div.attrs({
  className: 'container',
})`
  overflow: hidden;
`
const ActionImageContainer = styled.div.attrs({
  className: 'mb-2 ml-2',
})`
  z-index: 10;
`

const ImageListContainer = styled.div.attrs({
  className: 'col-12 col-lg-7 col-xl-7',
})`
  position: relative;
`

const ContentContainer = styled.div.attrs({
  className: 'col-12 pt-3 px-lg-3',
})``

const InfoContainer = styled.div.attrs({
  className: 'col-12 col-lg-5 col-xl-4',
})`
  margin-bottom: -3rem;
`

const AdditionalDescriptionAvent = styled.div.attrs({
  className:
    'd-flex flex-column align-items-center justify-content-center px-5',
})`
  & #flix-minisite {
    order: 2;
    & img {
      padding-right: 0;
    }
  }
`

const AdditionalDescriptionAventContainer = styled.div.attrs({
  className: 'd-flex flex-column',
})<{ expand: boolean; hasContent: boolean }>`
  display: ${({ hasContent }) => !hasContent && 'none !important'};
  & svg {
    width: 5rem;
    height: 5rem;
    stroke-width: 1;
    color: ${({ theme }) => theme.palette.greyTitle};
    align-self: center;

    cursor: pointer;
    ${({ expand }) =>
      expand
        ? css`
            transition: transform 0.2s cubic-bezier(0.36, 0.07, 0.19, 0.97);
            transform: rotate(180deg);
          `
        : ''}
  }
`
const FavIconWrapper = styled.div`
  color: ${({ theme }) => theme.palette.silverTree};
  cursor: pointer;
  position: absolute;
  top: 0;
  right: 0;
  z-index: 100;

  & svg {
    width: 3.2rem;
    height: 3.2rem;
  }
`

const DiscountIcon = styled.img<{ isInAction?: boolean }>`
  position: absolute;
  top: ${(props) => (props.isInAction ? '4rem' : '1rem')};
  left: 11.5rem;
  z-index: 100;
  width: 6rem;

  @media (max-width: ${({ theme }) => theme.breakpoints.md.max}) {
    left: 2.5rem;
    top: ${(props) => (props.isInAction ? '3.2rem' : '1rem')};
  }
`

const BadgeListContainer = styled.div.attrs({
  className: 'd-flex flex-column align-items-start',
})`
  position: absolute;
  top: 1rem;
  left: 10.8rem;
  @media (max-width: ${({ theme }) => theme.breakpoints.md.max}) {
    left: 0.8rem;
  }
`

const Badge = styled.div.attrs({
  className: 'py-0 px-2 mb-3',
})`
  min-width: 5.1rem;
  background-color: var(--badge-color);
  color: var(--badge-text-color);
  font-weight: bold;
  font-size: 1rem;
  height: 2.4rem;
  text-align: center;
  text-transform: uppercase;
  line-height: 2.4rem;
  z-index: 100;
  position: relative;

  & > small {
    font-size: 0.8rem;
  }

  &:after {
    content: '';
    position: absolute;
    right: -1.6rem;
    top: 0px;
    z-index: 14;
    width: 0;
    height: 0;
    border-style: solid;
    border-width: 2.4rem 1.6rem 0px 0px;
    border-color: var(--badge-color) transparent transparent transparent;
  }
`

const YellowBadge = styled(Badge)`
  --badge-color: ${({ theme }) => theme.palette.yellow};
  --badge-text-color: ${({ theme }) => theme.palette.secondary};
`

const BlueBadge = styled(Badge)`
  --badge-color: #1480c8;
  --badge-text-color: ${({ theme }) => theme.palette.secondary};
`

const NewBadge = styled(Badge)`
  --badge-color: ${({ theme }) => theme.palette.discounted};
  --badge-text-color: ${({ theme }) => theme.palette.secondary};
`

const DiscountBadge = styled(Badge)`
  --badge-color: ${({ theme }) => theme.palette.discounted};
  --badge-text-color: ${({ theme }) => theme.palette.secondary};
`

const MostSaled = styled(Badge)`
  --badge-color: ${({ theme }) => theme.palette.salmon};
  --badge-text-color: ${({ theme }) => theme.palette.secondary};
`
